import { useEffect, useState } from 'react';
import { complianceAPI, vehicleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, StatusBadge, Modal, Input, Select, Loading, Alert } from '../components/ui';

const TYPES = ['insurance','revenue_licence','emission_test','fitness_certificate','other'];

export default function Compliance() {
  const { can } = useAuth();
  const [items,    setItems]    = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [filterType,  setFilterType]  = useState('');
  const [filterStatus,setFilterStatus]= useState('');
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ vehicle_id:'', type:'insurance', document_no:'', issue_date:'', expiry_date:'', issued_by:'', status:'valid', notes:'' });
  const [saving,   setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([complianceAPI.getAll({ type: filterType, status: filterStatus, limit:100 }), vehicleAPI.getAll({ limit:100 })])
      .then(([c,v]) => { setItems(c.data.data); setVehicles(v.data.data); })
      .catch(() => setError('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filterType, filterStatus]);

  const openAdd  = () => { setEditing(null); setForm({ vehicle_id:'', type:'insurance', document_no:'', issue_date:'', expiry_date:'', issued_by:'', status:'valid', notes:'' }); setModal(true); };
  const openEdit = (i) => { setEditing(i); setForm({ ...i }); setModal(true); };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editing ? await complianceAPI.update(editing.id, form) : await complianceAPI.create(form);
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Compliance Management" sub={`${items.length} records`}
        action={can('admin','transport_officer') && <Button onClick={openAdd}>+ Add Record</Button>} />
      {error && <Alert message={error} className="mb-4" />}
      <div className="flex gap-3 mb-5 flex-wrap">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
          <option value="">All types</option>
          {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
          <option value="">All statuses</option>
          {['valid','expiring_soon','expired'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
      </div>
      {loading ? <Loading /> : (
        <Table headers={['Vehicle','Type','Document No.','Issue Date','Expiry Date','Status','Actions']}>
          {items.map(i => (
            <tr key={i.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-mono text-primary-600 text-sm">{i.vehicle?.reg_number}</td>
              <td className="px-4 py-3 text-sm capitalize">{i.type?.replace(/_/g,' ')}</td>
              <td className="px-4 py-3 text-sm">{i.document_no || '—'}</td>
              <td className="px-4 py-3 text-sm">{i.issue_date  || '—'}</td>
              <td className="px-4 py-3 text-sm font-medium">{i.expiry_date}</td>
              <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
              <td className="px-4 py-3">
                {can('admin','transport_officer') && <Button variant="ghost" size="sm" onClick={() => openEdit(i)}>Edit</Button>}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Compliance Record' : 'Add Compliance Record'}>
        <form onSubmit={save} className="space-y-4">
          <Select label="Vehicle *" value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})} required>
            <option value="">Select vehicle…</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number} — {v.brand} {v.model}</option>)}
          </Select>
          <Select label="Type *" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
            {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
          </Select>
          <Input label="Document Number" value={form.document_no} onChange={e => setForm({...form, document_no: e.target.value})} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Issue Date"   type="date" value={form.issue_date}   onChange={e => setForm({...form, issue_date:   e.target.value})} />
            <Input label="Expiry Date *" type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Issued By" value={form.issued_by} onChange={e => setForm({...form, issued_by: e.target.value})} />
            <Select label="Status" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
              {['valid','expiring_soon','expired'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </Select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
